import * as z from 'zod'

export const familySchema = z.object({
  lastName: z.string().min(1, { message: 'required' }),
  firstName: z.string().min(1, { message: 'required' }),
  lastNameKana: z
    .string()
    .min(1)
    .regex(/^[\u30A0-\u30FF]+$/, { message: 'must kana' }),
  firstNameKana: z
    .string()
    .min(1)
    .regex(/^[\u30A0-\u30FF]+$/, { message: 'must kana' }),
  phone: z
    .string()
    .regex(/\d+$/, { message: 'must number' })
    .min(10, { message: 'too small' })
    .max(11, { message: 'too large' }),
  postalCode: z.string().regex(/\d{7}$/, { message: 'not postalCode' }),
  isAnotherPerson: z.literal(true),
  email: z.string().email('required').optional()
})
export const paymentSchema = z.object({
  paymentTerm: z.enum(["monthly", "yearly"]),
  paymentMethod: z.enum(["creditCard", "directDebit"])
})
const noFamilySchema = z.object({
  lastName: z.string().min(1),
  firstName: z.string().min(1),
  phone: z.string()
})
const contractorSchema = z.object({
  lastName: z.string().min(1, { message: 'required' }),
  firstName: z.string().min(1, { message: 'required' }),
  lastNameKana: z
    .string()
    .min(1)
    .regex(/^[\u30A0-\u30FF]+$/, { message: 'must kana' }),
  firstNameKana: z
    .string()
    .min(1)
    .regex(/^[\u30A0-\u30FF]+$/, { message: 'must kana' }),
  postalCode: z.string().regex(/\d{7}$/, { message: 'not postalCode' }),
  address: z.string().min(1, { message: 'required' }),
  phone: z
    .string()
    .regex(/\d+$/, { message: 'must number' })
    .min(10, { message: 'too small' })
    .max(11, { message: 'too large' }),
  email: z.string().email('required')
})
const userSchemaBase = {
  lastName: z.string().min(1, { message: 'required' }),
  firstName: z.string().min(1, { message: 'required' }),
  lastNameKana: z
    .string()
    .min(1)
    .regex(/^[\u30A0-\u30FF]+$/, { message: 'must kana' }),
  firstNameKana: z
    .string()
    .min(1)
    .regex(/^[\u30A0-\u30FF]+$/, { message: 'must kana' }),
  postalCode: z.string().regex(/\d{7}$/, { message: 'not postalCode' }),
  address: z.string().min(1, { message: 'required' }),
  place: z.string().min(1, { message: 'required' }),
  placeNumber: z.string().min(1, { message: 'required' }),
  phone: z
    .string()
    .regex(/(060|070|080|090)\d{8}$/, { message: 'must mobile' }),
  entry: z.string().min(1, { message: 'required' }),
  necessaryEKYC: z.boolean()
}
const userSchemaBaseWithEmail = z.object({
  necessaryEmail: z.literal(true),
  ...userSchemaBase,
  email: z.string().email('required')
})
const agreementSchema = z.literal(true, {
  errorMap: () => ({ message: 'required' })
})
export const userSchemasWithContractor = [
  userSchemaBaseWithEmail.merge(
    z.object({
      necessaryContractor: z.literal(true),
      noFamilies: z.literal(false)
    })
  ),
  userSchemaBaseWithEmail.merge(
    z.object({
      necessaryContractor: z.literal(true),
      noFamilies: z.literal(false),
      contractor: contractorSchema
    })
  ),
  userSchemaBaseWithEmail.merge(
    z.object({
      necessaryContractor: z.literal(true),
      noFamilies: z.literal(false),
      contractor: contractorSchema,
      agreement: agreementSchema,
      families: z.array(familySchema)
    })
  ),
  userSchemaBaseWithEmail.merge(
    z.object({
      necessaryContractor: z.literal(true),
      noFamilies: z.literal(false),
      contractor: contractorSchema,
      agreement: agreementSchema,
      families: z.array(familySchema)
    })
  )
]
export const userSchemasWithContractorNoFamily = [
  userSchemaBaseWithEmail.merge(
    z.object({
      necessaryContractor: z.literal(true),
      noFamilies: z.literal(true)
    })
  ),
  userSchemaBaseWithEmail.merge(
    z.object({
      necessaryContractor: z.literal(true),
      noFamilies: z.literal(true),
      contractor: contractorSchema
    })
  ),
  userSchemaBaseWithEmail.merge(
    z.object({
      necessaryContractor: z.literal(true),
      noFamilies: z.literal(true),
      contractor: contractorSchema,
      agreement: agreementSchema,
      families: z.array(noFamilySchema)
    })
  ),
  userSchemaBaseWithEmail.merge(
    z.object({
      necessaryContractor: z.literal(true),
      noFamilies: z.literal(true),
      contractor: contractorSchema,
      agreement: agreementSchema,
      families: z.array(noFamilySchema)
    })
  )
]
export const userSchemasWithoutContractor = [
  userSchemaBaseWithEmail.merge(
    z.object({
      necessaryContractor: z.literal(false),
      noFamilies: z.literal(false)
    })
  ),
  userSchemaBaseWithEmail.merge(
    z.object({
      necessaryContractor: z.literal(false),
      noFamilies: z.literal(false),
      agreement: agreementSchema,
      families: z.array(familySchema)
    })
  ),
  userSchemaBaseWithEmail.merge(
    z.object({
      necessaryContractor: z.literal(false),
      noFamilies: z.literal(false),
      agreement: agreementSchema,
      families: z.array(familySchema)
    })
  )
]
export const userSchemasWithoutContractorNoFamily = [
  userSchemaBaseWithEmail.merge(
    z.object({
      necessaryContractor: z.literal(false),
      noFamilies: z.literal(true)
    })
  ),
  userSchemaBaseWithEmail.merge(
    z.object({
      necessaryContractor: z.literal(false),
      noFamilies: z.literal(true),
      agreement: agreementSchema,
      families: z.array(noFamilySchema)
    })
  ),
  userSchemaBaseWithEmail.merge(
    z.object({
      necessaryContractor: z.literal(false),
      noFamilies: z.literal(true),
      agreement: agreementSchema,
      families: z.array(noFamilySchema)
    })
  )
]
const userSchemaAllWithEmail = userSchemaBaseWithEmail.merge(
  z.object({
    necessaryContractor: z.boolean(),
    noFamiies: z.boolean().optional(),
    contractor: contractorSchema,
    agreement: agreementSchema,
    families: z.array(familySchema)
  })
)
export const userSchemaAll = userSchemaAllWithEmail
